/**
 * Classement tournoi de l'EFFICACITÉ des équipes (calculé côté client depuis l'état
 * officiel) : attaques les plus cliniques (buts − xG) et défenses les plus solides
 * (encaissés − xG adverses). Réutilise `computeAttackEfficiency` (buts réels vs attendus).
 * Masqué tant que trop peu d'équipes ont une donnée xG exploitable. Lignes `data-team`
 * → la fiche pays au clic se branche dessus gratuitement.
 */
import type { OfficialState } from "../../app/officialStore.ts";
import { teams } from "../../app/data.ts";
import { computeAttackEfficiency, type AttackEfficiency } from "../../app/metrics.ts";
import { esc, flagCode, teamName } from "../html.ts";
import { t } from "../../app/i18n.ts";

/** Côté (0 = home, 1 = away) de l'équipe dans un match, ou null. */
function sideOf(state: OfficialState, code: string, v: OfficialState["matches"][number]): 0 | 1 | null {
  if (v.match.stage === "group") return v.match.home === code ? 0 : v.match.away === code ? 1 : null;
  const n = state.bracket["M" + String(v.match.id)];
  return n?.a === code ? 0 : n?.b === code ? 1 : null;
}

interface TeamEff {
  readonly code: string;
  readonly attack: AttackEfficiency | null;
  readonly defense: AttackEfficiency | null;
}

function teamEff(state: OfficialState, code: string): TeamEff {
  const att: { goals: number; xg: number | null; shots: number | null }[] = [];
  const def: { goals: number; xg: number | null; shots: number | null }[] = [];
  for (const v of state.matches) {
    const i = sideOf(state, code, v);
    if (i === null) continue;
    const ft = v.displayScore ?? v.live?.score?.ft ?? null;
    if (!ft) continue;
    const s = v.live?.stats;
    const o = i === 0 ? 1 : 0;
    att.push({ goals: ft[i] ?? 0, xg: s?.xg?.[i] ?? null, shots: s?.shots?.[i] ?? null });
    def.push({ goals: ft[o] ?? 0, xg: s?.xg?.[o] ?? null, shots: s?.shots?.[o] ?? null });
  }
  return { code, attack: computeAttackEfficiency(att), defense: computeAttackEfficiency(def) };
}

function rowHtml(r: TeamEff, eff: AttackEfficiency, i: number, kind: "att" | "def"): string {
  const gain = `${eff.gain >= 0 ? "+" : "−"}${Math.abs(eff.gain).toFixed(1)}`;
  // attaque : marquer PLUS que xG = bon (vert) ; défense : encaisser MOINS = bon (vert).
  const good = kind === "att" ? eff.tone === "over" : eff.tone === "under";
  const bad = kind === "att" ? eff.tone === "under" : eff.tone === "over";
  const main = kind === "att" ? t("teff.main.goals", { n: eff.goals }) : t("teff.main.conceded", { n: eff.goals });
  return `<li class="teff-row${i < 3 ? ` r${i + 1}` : ""}" data-team="${esc(r.code)}" tabindex="0" role="button" title="${esc(teamName(r.code))}">
    <span class="teff-rk">${i + 1}</span>
    <span class="f fi fi-${esc(flagCode(r.code))}"></span><span class="teff-cd">${esc(r.code)}</span>
    <span class="teff-main">${main} · ${eff.xg.toFixed(1)} xG</span>
    <b class="teff-gain ${good ? "good" : bad ? "bad" : ""}">${gain}</b>
  </li>`;
}

function col(title: string, rows: TeamEff[], kind: "att" | "def"): string {
  const items = rows
    .map((r, i) => rowHtml(r, (kind === "att" ? r.attack : r.defense) as AttackEfficiency, i, kind))
    .join("");
  return `<div class="teff-col"><div class="teff-h">${esc(title)}</div><ol class="teff-list">${items}</ol></div>`;
}

export function renderTeamEfficiency(el: HTMLElement, state: OfficialState): void {
  const all = teams.map((t) => teamEff(state, t.id));
  // On ne classe QUE les équipes avec assez d'occasions (xG cumulé ≥ 3) : sur 1 match,
  // l'écart buts−xG est surtout du hasard — classer ce bruit serait trompeur.
  const attacks = all.filter((r) => r.attack?.confident).sort((a, b) => (b.attack as AttackEfficiency).gain - (a.attack as AttackEfficiency).gain);
  // défense : gain = encaissés − xGA ; plus c'est NÉGATIF, plus la défense surperforme.
  const defenses = all.filter((r) => r.defense?.confident).sort((a, b) => (a.defense as AttackEfficiency).gain - (b.defense as AttackEfficiency).gain);
  if (attacks.length < 3 && defenses.length < 3) {
    el.innerHTML = "";
    el.classList.add("hide");
    return;
  }
  el.classList.remove("hide");
  el.innerHTML = `<h2 class="sec-title">${t("teff.section.title")} <span class="count">${t("teff.section.count")}</span></h2>
    <div class="teff card">
      ${col(t("teff.col.attack"), attacks.slice(0, 6), "att")}
      ${col(t("teff.col.defense"), defenses.slice(0, 6), "def")}
    </div>
    <p class="note teff-cav">${t("teff.caveat")}</p>`;
}
